import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Segmented } from '@hp/ui';
import { useMe } from '../../../api/me';
import { trpc, type RouterInputs } from '../../../api/trpc';
import { ErrorAlert } from '../../../components/error-alert';
import { PdLink } from '../../../components/pd-link';
import { StatusBadge } from '../../../components/badges';
import { fmtDateTime } from '../../../lib/format';
import type { Fmt } from './format';

type Kind = RouterInputs['performance']['qualityIssues']['kind'];
const KINDS = ['no_source', 'no_campaign', 'unmapped_stage'] as const satisfies readonly Kind[];

/** The records behind the quality score: newest first, each linked to its Pipedrive deal. */
export function QualityIssues({ from, to, fmt }: { from: string; to: string; fmt: Fmt }) {
  const { t } = useTranslation(['performance', 'common']);
  const me = useMe();
  const [kind, setKind] = useState<Kind>('no_source');
  const [open, setOpen] = useState(false);
  const issues = useQuery(
    trpc.performance.qualityIssues.queryOptions({ from, to, kind, limit: 200 }, { enabled: open, staleTime: 60_000 }),
  );
  return (
    <details className="pa-data-details" onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>{t('dashboard.issues.summary')}</summary>
      <div className="bo-sections">
        <Segmented
          ariaLabel={t('dashboard.issues.kindAria')}
          value={kind}
          options={KINDS.map((k) => ({ value: k, label: t(`dashboard.issues.kind.${k}`) }))}
          onChange={setKind}
        />
        <ErrorAlert error={issues.error} className="bo-alert--inset" />
        {issues.data && (
          <p className="bo-muted">
            {t('dashboard.issues.total', { count: issues.data.total })}
            {issues.data.total > issues.data.rows.length &&
              ` ${t('dashboard.issues.showing', { shown: fmt.int(issues.data.rows.length), total: fmt.int(issues.data.total) })}`}
          </p>
        )}
        {issues.data && issues.data.rows.length === 0 ? (
          <p className="pa-empty">{t('dashboard.issues.empty')}</p>
        ) : (
          issues.data && (
            <div className="pa-table-scroll" tabIndex={0}>
              <table className="pa-table hp-table">
                <thead>
                  <tr>
                    <th scope="col">{t('dashboard.issues.deal')}</th>
                    <th scope="col">{t('dashboard.issues.created')}</th>
                    <th scope="col">{t('dashboard.issues.pipeline')}</th>
                    <th scope="col">{t('dashboard.issues.source')}</th>
                    <th scope="col">{t('dashboard.issues.campaign')}</th>
                    <th scope="col">{t('dashboard.issues.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.data.rows.map((r) => (
                    <tr key={r.pdId}>
                      <td>
                        <PdLink domain={me.data?.pipedriveCompanyDomain} entity="deal" pdId={r.pdId}>
                          {r.title ?? `#${r.pdId}`}
                        </PdLink>
                      </td>
                      <td className="bo-mono">{r.addTime ? fmtDateTime(r.addTime) : '—'}</td>
                      <td>{r.pipelineName ?? r.pipelinePdId ?? '—'}</td>
                      <td>{r.sourceLabel ?? <span className="bo-muted">—</span>}</td>
                      <td>{r.campaignLabel ?? <span className="bo-muted">—</span>}</td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
        <p className="pa-footnote">{t('dashboard.issues.hint')}</p>
      </div>
    </details>
  );
}
